import { Switch, Route } from "wouter";
import { lazy, Suspense } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DebugProvider } from "@/contexts/debug-context";
import { JobProvider } from "@/contexts/job-context";
import { FloatingPanelProvider } from "@/contexts/floating-panel-context";
import FloatingJobStatus from "@/components/FloatingJobStatus";
import { DebugModals } from "@/components/debug/DebugModals";
import { DebugErrorBoundary } from "@/components/debug/DebugErrorBoundary";
import { WhatsNewDialog } from "@/components/WhatsNewDialog";
import { lazyWithReload } from "@/lib/chunk-reload";
const Home = lazy(lazyWithReload(() => import("@/pages/home")));
const Account = lazy(lazyWithReload(() => import("@/pages/account")));
const Characters = lazy(lazyWithReload(() => import("@/pages/characters")));
const CharacterView = lazy(lazyWithReload(() => import("@/pages/character-view")));
const AdminPage = lazy(lazyWithReload(() => import("@/pages/admin")));
const LegalImprintPage = lazy(lazyWithReload(() => import("@/pages/legal-imprint")));
const LegalPrivacyPage = lazy(lazyWithReload(() => import("@/pages/legal-privacy")));
const LegalTermsPage = lazy(lazyWithReload(() => import("@/pages/legal-terms")));
import { ThemeProvider } from "@/contexts/theme-context";
import { AuthProvider } from "@/contexts/auth-context";
const NotFound = lazy(lazyWithReload(() => import("@/pages/not-found")));
import { AdminSessionProvider } from "@/contexts/admin-session-context";
import { useAuth } from "@/hooks/use-auth";

function Router() {
  return (
    <Suspense fallback={<div />}> 
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/account" component={Account} />
        <Route path="/characters/share/:token">
          {(params) => <CharacterView token={params.token} />}
        </Route>
        <Route path="/characters/:id">
          {(params) => <CharacterView id={params.id} />}
        </Route>
        <Route path="/characters" component={Characters} />
        <Route path="/legal/imprint" component={LegalImprintPage} />
        <Route path="/legal/privacy" component={LegalPrivacyPage} />
        <Route path="/legal/terms" component={LegalTermsPage} />
        <Route path="/admin" component={AdminPage} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function WhatsNewHandler() {
  const { profile, updateUserLastActive, replaceSettings } = useAuth();
  const isDev = import.meta.env.DEV;

  if (isDev) {
    console.log("[WhatsNewHandler] Rendering", { 
      hasProfile: !!profile, 
      lastActive: profile?.lastActive,
      showUpdates: profile?.settings?.showUpdates,
      hasUpdateUserLastActive: typeof updateUserLastActive === 'function'
    });
  }

  const handleDismiss = async () => {
    if (isDev) console.log("[WhatsNewHandler] handleDismiss called - storing dismiss timestamp");

    if (!profile) return;
    
    // Store the dismiss timestamp in settings
    try {
      const now = new Date().toISOString();
      await replaceSettings({
        ...profile.settings,
        lastUpdatesDismissed: now,
      });
      await updateUserLastActive();
      if (isDev) console.log("[WhatsNewHandler] Stored dismiss timestamp:", now);
    } catch (error) {
      if (isDev) console.error("[WhatsNewHandler] Failed to store dismiss timestamp:", error);
    }
  };

  const handleToggleShowUpdates = async (show: boolean) => {
    if (!profile) return;
    
    if (isDev) console.log("[WhatsNewHandler] Toggling showUpdates to:", show);
    try {
      await replaceSettings({
        ...profile.settings,
        showUpdates: show,
      });
    } catch (error) {
      if (isDev) console.error("Failed to update showUpdates setting", error);
    }
  };

  if (!profile) {
    if (isDev) console.log("[WhatsNewHandler] No profile, not rendering dialog");
    return null;
  }

  return (
    <WhatsNewDialog
      lastActive={String(profile.settings?.lastUpdatesDismissed || profile.createdAt || '') || null}
      showUpdates={profile.settings?.showUpdates !== false}
      onDismiss={handleDismiss}
      onToggleShowUpdates={handleToggleShowUpdates}
    />
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <DebugErrorBoundary>
        <DebugProvider>
          <JobProvider>
            <AuthProvider>
              <ThemeProvider>
                <AdminSessionProvider>
                  <FloatingPanelProvider>
                    <TooltipProvider>
                      <Toaster />
                      <Router />
                      <FloatingJobStatus />
                      <DebugModals />
                      <WhatsNewHandler />
                    </TooltipProvider>
                  </FloatingPanelProvider>
                </AdminSessionProvider>
              </ThemeProvider>
            </AuthProvider>
          </JobProvider>
        </DebugProvider>
      </DebugErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
